import { toast } from '@/components/toast';
import { createCampaign, deleteAgent, uploadFile } from '@/services/api';
import { logger } from '@/services/logger';
import { getTtsDuration } from '@/services/tts/duration';
import { IVoice, Presenter, StreamTextToSpeechVoice, TtsInitResponse, VideoType } from '@d-id/types';
import { DEFAULT_CAMPAIGN_NAME } from '../constants';
import { getPreviewText } from './components/Content/VoiceScript/RichEditor/utils';
import { CAMPAIGN_DRAFT_AGENT_ID } from './constants';
import { CampaignFormValues } from './types';

export const deleteExistingDraftAgent = async ({ removeFromDB = true }: { removeFromDB?: boolean } = {}) => {
    const draftCampaignAgent = localStorage.getItem(CAMPAIGN_DRAFT_AGENT_ID);

    if (!draftCampaignAgent) {
        return;
    }

    if (removeFromDB) {
        try {
            await deleteAgent(draftCampaignAgent);
        } catch (error) {
            logger.error('Failed to delete campaign draft agent', { id: draftCampaignAgent, error });
        }
    }

    localStorage.removeItem(CAMPAIGN_DRAFT_AGENT_ID);
};

interface EnrichPresenterParams {
    presenter: Presenter;
    voice: IVoice;
    isStitch?: boolean;
}

type SupportedVideoTypes = VideoType.CLIP | VideoType.TALK;

export const enrichPresenter = ({ presenter: rawPresenter, voice: rawVoice, isStitch }: EnrichPresenterParams) => {
    const presenterInfoAdapter: Record<
        SupportedVideoTypes,
        | { type: 'talk'; source_url: string; stitch?: boolean }
        | { type: 'clip'; presenter_id: string; driver_id?: string; source_url: string }
    > = {
        [VideoType.TALK]: {
            type: 'talk',
            source_url: rawPresenter?.poster,
            stitch: isStitch,
        },
        [VideoType.CLIP]: {
            type: 'clip',
            presenter_id: rawPresenter?.id,
            driver_id: rawPresenter?.driverId,
            source_url: rawPresenter.thumbnail || rawPresenter.poster,
        },
    };

    const presenterType = (rawPresenter?.type as SupportedVideoTypes) ?? VideoType.TALK;

    const voice: StreamTextToSpeechVoice = {
        type: rawVoice.provider,
        voice_id: rawVoice.id,
        access: rawVoice.access,
        language: rawVoice.language,
    };

    const presenter = {
        voice,
        thumbnail: rawPresenter?.thumbnail || rawPresenter?.poster || '',
        ...presenterInfoAdapter[presenterType],
    };

    return presenter;
};

export const onFormSubmit = async ({
    data,
    onSuccess,
    onError,
    ttsData,
    languageToVoices,
}: {
    data: CampaignFormValues;
    onSuccess: () => void;
    onError: () => void;
    ttsData?: TtsInitResponse;
    languageToVoices: Record<string, IVoice[]>;
}) => {
    const { is_stitch, presenter: rawPresenter, voice, logo_src, agent, name, ...rest } = data;

    const duration = await getTtsDuration({
        setInlineTtsError: () => {},
        text: getPreviewText(rest.script),
        voice,
        credentials: ttsData?.credentials,
        languageToVoices,
    });

    const presenter = enrichPresenter({
        presenter: rawPresenter,
        voice,
        isStitch: is_stitch,
    });

    const logo = logo_src instanceof File ? await uploadFile(logo_src) : logo_src;

    const payload = {
        ...rest,
        presenter,
        logo_src: logo,
        agent_id: agent.id,
        duration,
        name: name?.trim() || DEFAULT_CAMPAIGN_NAME,
    };

    try {
        await createCampaign(payload);
        toast.success({ title: 'Campaign created successfully', toastId: 'create-campaign-success' });
        onSuccess();
    } catch (error) {
        logger.error('Failed to create campaign', error);
        toast.error({ title: 'Failed to create campaign, please try again', toastId: 'create-campaign-error' });
        onError();
    }
};
